/**
 * Optimizes an image blob for storage: limits max dimension and re-encodes as JPEG
 * with high quality to reduce file size while keeping visual quality.
 *
 * @param {Blob} blob - Image blob (File or Blob)
 * @param {number} maxDimension - Max width or height in pixels (default 1920)
 * @param {number} quality - JPEG quality 0-1 (default 0.92)
 * @returns {Promise<Blob>} Optimized JPEG blob, or original blob on error
 */
export function optimizeImageBlob(blob, maxDimension = 1920, quality = 0.92) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth;
      const h = img.naturalHeight;

      if (!w || !h) {
        resolve(blob);
        return;
      }

      const scale = Math.min(maxDimension / w, maxDimension / h, 1);
      const outW = Math.round(w * scale);
      const outH = Math.round(h * scale);

      try {
        const canvas = document.createElement("canvas");
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(blob);
          return;
        }
        ctx.drawImage(img, 0, 0, outW, outH);
        canvas.toBlob(
          (optimized) => {
            if (optimized) resolve(optimized);
            else resolve(blob);
          },
          "image/jpeg",
          quality
        );
      } catch {
        resolve(blob);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(blob);
    };

    img.src = url;
  });
}
